import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AddNewPopModel, PasswordComplexityConditionModel } from '../../models/pop-up.interface';
import { PasswordComplexityValidation } from '../../utilities/password-complexity.validator';
import { MatLabel, MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { NgClass } from '@angular/common';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'app-agilysys-change-password-popup',
    templateUrl: './agilysys-change-password-popup.component.html',
    styleUrl: './agilysys-change-password-popup.component.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [ReactiveFormsModule, MatLabel, MatFormField, MatInput, MatSuffix, NgClass, MatButton, TranslateModule]
})
export class AgilysysChangePasswordPopupComponent implements OnInit {

  public dialogContent: any;
  homeGroup: FormGroup;
  errorMessage = '';
  newPasswordErrorMessage = '';
  strengthBarValidCount = null;
  weakStrengthBarCount = null;
  mediumStrengthBarCount = null;
  strongStrengthBarCount = null;
  strengthBarText = '';
  strongStrengthBarPercent = null;
  minLength = 6;
  hidePassword: boolean = true;
  hideNewPW: boolean = true;

  constructor(
    public dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: AddNewPopModel,
    public formBuilder: FormBuilder, public translate: TranslateService
  ) {
    this.dialogContent = data;
    const _that = this;
    if (this.data.FormGroupData) {
      setTimeout(function () { _that.homeGroup.patchValue(_that.data.FormGroupData); }, 1000);
    }

    this.homeGroup = this.formBuilder.group({
      newPassword: ['', [
        Validators.required,
        Validators.minLength(this.minLength)
      ]],
      confirmPassword: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    const _that = this;
    this.homeGroup.controls['newPassword'].valueChanges.subscribe(res => {
      if (res) {
        _that.errorMessage = '';
        _that.newPasswordErrorMessage = '';
        _that.getPasswordComplexityCondition(res);
      } else {
        _that.data.PasswordComplexityConditions.map(x => x.ValidIcon = null);
        _that.data.PasswordComplexityConditions = [..._that.data.PasswordComplexityConditions];
        _that.strengthBarText = '';
        _that.strongStrengthBarCount = null;
        _that.weakStrengthBarCount = null;
        _that.mediumStrengthBarCount = null;
        _that.strengthBarValidCount = null;
        _that.strongStrengthBarPercent = null;
        _that.errorMessage = '';
        _that.newPasswordErrorMessage = '';
      }
    });
    this.homeGroup.controls['confirmPassword'].valueChanges.subscribe(res => {
      if (res) {
        _that.errorMessage = '';
        _that.newPasswordErrorMessage = '';
      }
    });
  }

  hideNewPassword(event) {
    this.hideNewPW = !this.hideNewPW;
    event.preventDefault();
  }

  hideConfirmPassword(event) {
    this.hidePassword = !this.hidePassword;
    event.preventDefault();
  }

  close() {
    this.dialogRef.close();
  }

  getPasswordComplexityCondition(res: any) {
    try {
      this.data.PasswordComplexityConditions.forEach((passwordComp: PasswordComplexityConditionModel) => {
        passwordComp.ValidIcon = false;
        if (passwordComp.Name === 'MinimumLength') {
          this.minLength = passwordComp.Value;
          passwordComp.ValidIcon = new PasswordComplexityValidation.MinimumLengthValidator().isAcceptable(res, this.minLength);
        }
        if (passwordComp.Name === 'MaximumLength') {
          passwordComp.ValidIcon = new PasswordComplexityValidation.MaximumLengthValidator().isAcceptable(res, this.minLength, passwordComp.Value);
        }
        if (passwordComp.Name === 'NumberAllowed') {
          const numberAllowedValid = new PasswordComplexityValidation.NumberAllowedValidator().isAcceptable(res);
          if (numberAllowedValid) {
            passwordComp.ValidIcon = numberAllowedValid;
          }
        }
        if (passwordComp.Name === 'UpperCaseCharacter') {
          const uppercaseValid = new PasswordComplexityValidation.UpperCaseValidator().isAcceptable(res);
          if (uppercaseValid) {
            passwordComp.ValidIcon = uppercaseValid;
          }
        }
        if (passwordComp.Name === 'LowerCaseCharacter') {
          const lowercaseValid = new PasswordComplexityValidation.LowerCaseValidator().isAcceptable(res);
          if (lowercaseValid) {
            passwordComp.ValidIcon = lowercaseValid;
          }
        }
        if (passwordComp.Name === 'SpecialCharacter') {
          const safeSpecialCharacterRegex = /[!@#$%^&*(),.?":{}|<>[\]\\\/';`~+=_\-]/g;
          const specialCharacterValid = new PasswordComplexityValidation.SpecialCharacterValidator().isAcceptable(res, safeSpecialCharacterRegex);
          if (specialCharacterValid) {
            passwordComp.ValidIcon = specialCharacterValid;
          }
        }
      });
      this.data.PasswordComplexityConditions = [...this.data.PasswordComplexityConditions];
      this.getPasswordStrengthBar();
    } catch (error) {
      console.error('Error occured at Get Valid Password Complexity condition - ', error);
    }
  }

  getPasswordStrengthBar() {
    try {
      this.strongStrengthBarCount = this.data.PasswordComplexityConditions.length;
      this.weakStrengthBarCount = ((this.strongStrengthBarCount / 3) / this.strongStrengthBarCount) * 100;
      this.mediumStrengthBarCount = ((this.strongStrengthBarCount / 2) / this.strongStrengthBarCount) * 100;
      this.strengthBarValidCount = (this.data.PasswordComplexityConditions.filter(x => x.ValidIcon === true).length / this.strongStrengthBarCount) * 100;
      this.strongStrengthBarPercent = ((this.strongStrengthBarCount / this.strongStrengthBarCount) * 100);
      if (this.strengthBarValidCount <= this.weakStrengthBarCount) {
        this.strengthBarText = 'Weak';
      } else if (this.strengthBarValidCount >= this.mediumStrengthBarCount &&
        this.strengthBarValidCount < this.strongStrengthBarPercent) {
        this.strengthBarText = 'Medium';
      } else if (this.strengthBarValidCount === this.strongStrengthBarPercent) {
        this.strengthBarText = 'Strong';
      } else {
        this.strengthBarText = '';
        this.strongStrengthBarCount = null;
        this.weakStrengthBarCount = null;
        this.mediumStrengthBarCount = null;
        this.strengthBarValidCount = null;
        this.strongStrengthBarPercent = null;
      }
    } catch (error) {
      console.error('Error occured in Get Password Strength Bar condition - ', error);
    }
  }

  changePassword(event) {
    const checkPasswordComplexityStatus = this.data.PasswordComplexityConditions.every(x => x.ValidIcon === true);
    if (checkPasswordComplexityStatus) {
      if (this.homeGroup?.valid) {
        if (this.homeGroup.value.newPassword === this.homeGroup.value.confirmPassword) {
          this.dialogRef.close(this.homeGroup);
        } else {
          this.errorMessage = this.translate.instant('PasswordComplexity.NoMatchPassword');
        }
      } else {
        this.homeGroup.markAllAsTouched();
      }
    } else {
      if (this.strengthBarText === 'Weak') {
        this.newPasswordErrorMessage = this.translate.instant('PasswordComplexity.WeakPasswordProvideStrongText');
      } else if (this.strengthBarText === 'Medium') {
        this.newPasswordErrorMessage = this.translate.instant('PasswordComplexity.MediumPasswordProvideStrongText');
      }
    }
  }

}
